import * as THREE from "three";
import { CELL } from "./loot";

/**
 * The dungeon room and the long hallway behind it. World frame: floor surface
 * at y = 0, the grid centred on the origin, x across the room (cols), z along
 * it (rows). The far edge (row 0, −z) is a tall wall pierced by a grand doorway
 * that opens onto a corridor receding into the dark — that corridor is where
 * the minotaur marches in.
 *
 * +z is toward the camera (foreground); −z is the background, so the hallway
 * and the approaching beast sit behind the room from the player's view. The
 * room's own side/front walls are kept to a low lip so they never occlude the
 * board from the three-quarter camera.
 */

export interface RoomRig {
  group: THREE.Group;
  cellToWorld(c: number, r: number): [number, number];
  /** z of the back edge (the doorway threshold). */
  zBack: number;
  /** z far down the hallway (where the minotaur spawns). */
  zFar: number;
  /** Centre width of the doorway opening (world units). */
  doorW: number;
  torchLights: THREE.PointLight[];
  /** Flame meshes (room + doorway) the Stage flickers. */
  flames: THREE.Mesh[];
}

export const cellToWorld = (cols: number, rows: number, c: number, r: number): [number, number] =>
  [(c - (cols - 1) / 2) * CELL, (r - (rows - 1) / 2) * CELL];

const stone = (color: number, rough = 0.96) =>
  new THREE.MeshStandardMaterial({ color, roughness: rough, metalness: 0.02 });

let floorTex: THREE.Texture | null = null;
function floorTexture(): THREE.Texture {
  const c = document.createElement("canvas");
  c.width = c.height = 256;
  const ctx = c.getContext("2d")!;
  ctx.fillStyle = "#1d150c";
  ctx.fillRect(0, 0, 256, 256);
  for (let i = 0; i < 1500; i++) {
    const v = 12 + Math.random() * 24;
    ctx.fillStyle = `rgba(${v + 16},${v + 9},${v},0.5)`;
    ctx.fillRect(Math.random() * 256, Math.random() * 256, 2, 2);
  }
  // a few mortar lines
  ctx.strokeStyle = "rgba(8,5,2,0.5)";
  ctx.lineWidth = 2;
  for (let i = 1; i < 4; i++) {
    ctx.beginPath(); ctx.moveTo(0, i * 64); ctx.lineTo(256, i * 64); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(i * 64, 0); ctx.lineTo(i * 64, 256); ctx.stroke();
  }
  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

function makeTorch(parent: THREE.Object3D, x: number, y: number, z: number,
  lights: THREE.PointLight[], flames: THREE.Mesh[], power = 7): void {
  const bracket = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.05, 0.5, 8),
    new THREE.MeshStandardMaterial({ color: 0x2a1c10, roughness: 0.8 }));
  bracket.position.set(x, y, z);
  bracket.rotation.x = 0.3;
  bracket.castShadow = true;
  parent.add(bracket);
  const flame = new THREE.Mesh(new THREE.SphereGeometry(0.12, 12, 10),
    new THREE.MeshBasicMaterial({ color: 0xffce6e }));
  flame.scale.set(1, 1.8, 1);
  flame.position.set(x, y + 0.32, z + 0.06);
  parent.add(flame);
  flames.push(flame);
  const light = new THREE.PointLight(0xffa94e, power, 12, 2);
  light.position.set(x, y + 0.45, z + 0.1);
  parent.add(light);
  lights.push(light);
}

export function buildRoom(cols: number, rows: number): RoomRig {
  const group = new THREE.Group();
  const torchLights: THREE.PointLight[] = [];
  const flames: THREE.Mesh[] = [];
  const W = cols * CELL, L = rows * CELL;
  const halfW = W / 2, halfL = L / 2;
  const zBack = -halfL;
  const hallLen = 11;
  const zFar = zBack - hallLen;
  const lipH = 0.55, lipT = 0.34;          // low room walls
  const backH = 3.4, backT = 0.45;         // tall backdrop wall
  const doorW = Math.min(2.9, W * 0.66);   // a grand doorway
  const doorH = 2.85;
  const hallH = 3.3, hallT = 0.5;

  floorTex ??= floorTexture();
  const ft = floorTex.clone();
  ft.needsUpdate = true;
  ft.repeat.set(cols / 2, rows / 2);
  const floorMat = new THREE.MeshStandardMaterial({ map: ft, color: 0x6f5b38, roughness: 0.98 });

  /* ---- room floor slab ---- */
  const floor = new THREE.Mesh(new THREE.BoxGeometry(W, 0.3, L), floorMat);
  floor.position.set(0, -0.15, 0);
  floor.receiveShadow = true;
  group.add(floor);

  /* ---- checker inlay so cells read ---- */
  const inlay = new THREE.Group();
  const tileGeo = new THREE.PlaneGeometry(CELL * 0.96, CELL * 0.96);
  for (let r = 0; r < rows; r++)
    for (let c = 0; c < cols; c++) {
      if ((r + c) % 2 === 0) continue;
      const t = new THREE.Mesh(tileGeo, new THREE.MeshStandardMaterial({
        color: 0x241a0e, roughness: 0.97, transparent: true, opacity: 0.5, depthWrite: false }));
      t.rotation.x = -Math.PI / 2;
      const [wx, wz] = cellToWorld(cols, rows, c, r);
      t.position.set(wx, 0.006, wz);
      t.receiveShadow = true;
      inlay.add(t);
    }
  group.add(inlay);

  /* ---- low room lips (sides + front) ---- */
  const lipMat = stone(0x40321d);
  const mkLip = (w: number, d: number, x: number, z: number) => {
    const m = new THREE.Mesh(new THREE.BoxGeometry(w, lipH, d), lipMat);
    m.position.set(x, lipH / 2 - 0.15, z);
    m.castShadow = true; m.receiveShadow = true;
    group.add(m);
  };
  mkLip(lipT, L, -halfW - lipT / 2, 0);
  mkLip(lipT, L, halfW + lipT / 2, 0);
  mkLip(W + lipT * 2, lipT, 0, halfL + lipT / 2);

  /* ---- tall back wall with a grand doorway ---- */
  const backMat = stone(0x463722);
  const segW = (W - doorW) / 2;
  const mkBack = (w: number, h: number, x: number, y: number) => {
    const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, backT), backMat);
    m.position.set(x, y, zBack - backT / 2);
    m.castShadow = true; m.receiveShadow = true;
    group.add(m);
  };
  mkBack(segW + lipT, backH, -(doorW / 2 + segW / 2) - lipT / 2, backH / 2 - 0.15);
  mkBack(segW + lipT, backH, (doorW / 2 + segW / 2) + lipT / 2, backH / 2 - 0.15);
  mkBack(doorW, backH - doorH, 0, doorH + (backH - doorH) / 2 - 0.15); // lintel above the door
  // arched keystone hint over the doorway
  const arch = new THREE.Mesh(new THREE.CylinderGeometry(doorW * 0.52, doorW * 0.52, backT + 0.06, 20, 1, false, 0, Math.PI),
    stone(0x3a2c18));
  arch.rotation.z = Math.PI;
  arch.position.set(0, doorH - 0.15, zBack - backT / 2);
  arch.scale.set(1, 0.18, 1);
  group.add(arch);

  /* ---- the hallway: floor + tall side walls + ceiling, receding ---- */
  const hallMat = stone(0x33291a);
  const hallFloor = new THREE.Mesh(new THREE.BoxGeometry(doorW + 0.2, 0.3, hallLen), floorMat);
  hallFloor.position.set(0, -0.15, zBack - hallLen / 2);
  hallFloor.receiveShadow = true;
  group.add(hallFloor);
  for (const sx of [-1, 1]) {
    const hw = new THREE.Mesh(new THREE.BoxGeometry(hallT, hallH, hallLen), hallMat);
    hw.position.set(sx * (doorW / 2 + hallT / 2), hallH / 2 - 0.15, zBack - hallLen / 2);
    hw.castShadow = true; hw.receiveShadow = true;
    group.add(hw);
  }
  const ceil = new THREE.Mesh(new THREE.BoxGeometry(doorW + hallT * 2, 0.3, hallLen), stone(0x140e07));
  ceil.position.set(0, hallH - 0.15, zBack - hallLen / 2);
  group.add(ceil);
  // a receding line of wall torches lights the corridor, so the minotaur is
  // seen walking through pools of light the whole way in
  for (let i = 0; i < 3; i++) {
    const z = zBack - 2.4 - i * 3.0;
    const sx = i % 2 === 0 ? -1 : 1;
    makeTorch(group, sx * (doorW / 2 - 0.12), 1.75, z, torchLights, flames, 6.5);
  }

  /* ---- doorway torches (frame the beast as it nears) + room torches ---- */
  makeTorch(group, -(doorW / 2) - 0.18, 1.7, zBack + 0.05, torchLights, flames, 9);
  makeTorch(group, (doorW / 2) + 0.18, 1.7, zBack + 0.05, torchLights, flames, 9);

  return {
    group,
    cellToWorld: (c, r) => cellToWorld(cols, rows, c, r),
    zBack, zFar, doorW, torchLights, flames,
  };
}
